import React, { useContext, useEffect, useState } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Form, Button, Spinner } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import TrainingTableComponent from "../../../components/tables/TrainingTableComponent";
import axios from "axios";

export default function Trainings() {
  const { t } = useContext(TranslatorContext);

  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [instituteFilter, setInstituteFilter] = useState("");

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    from: 0,
    to: 0,
  });
  const [filterOptions, setFilterOptions] = useState({
    statuses: [],
    courses: [],
    institutes: [],
  });

  const AuthToken = localStorage.getItem("AuthUser");

  axios.defaults.headers.common[
    "Authorization"
  ] = `Bearer ${AuthToken}`;

  // wait for the user to stop typing before hitting the API
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 500);

    return () => clearTimeout(delay);
  }, [search]);

  useEffect(() => {
    let ignore = false;

    const params = { page };
    if (debouncedSearch) params.search = debouncedSearch;
    if (statusFilter) params.status = statusFilter;
    if (courseFilter) params.course_id = courseFilter;
    if (instituteFilter) params.institute_id = instituteFilter;

    setLoading(true);

    axios
      .get(
        process.env.REACT_APP_API_URL +
        "/vocational-trainings",
        { params }
      )
      .then((res) => {
        if (ignore) return;

        const mappedData = res.data.data.map(
          (training) => ({
            id: training.id,
            f_number: training.f_number,
            student_name:
              training.student_name,
            relation: training.relation,
            institute_name:
              training.institute_name,
            training_name:
              training.training_name,
            training_duration:
              training.duration,
            total_fees: training.fee,
            status: training.completed,
            action: {
              edit: "edit",
              delete: "delete",
            },
          })
        );

        setTrainings(mappedData);
        // fall back to a single page if the API has not been updated yet
        setPagination(
          res.data.pagination || {
            current_page: 1,
            last_page: 1,
            total: mappedData.length,
            from: mappedData.length ? 1 : 0,
            to: mappedData.length,
          }
        );
        if (res.data.filters) setFilterOptions(res.data.filters);
      })
      .catch((error) => {
        if (!ignore) console.error(error);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [page, debouncedSearch, statusFilter, courseFilter, instituteFilter]);

  // also show the loader while the search box is waiting for its debounce
  const busy = loading || search.trim() !== debouncedSearch;

  const changeFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter("");
    setCourseFilter("");
    setInstituteFilter("");
    setPage(1);
  };

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">
                Vocational Trainings ({pagination.total})
              </h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link
                    to="#"
                    className="mc-breadcrumb-link"
                  >
                    {t("home")}
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  <Link
                    to="#"
                    className="mc-breadcrumb-link"
                  >
                    {t(
                      "Vocational Trainings"
                    )}
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  All Trainings List
                </li>
              </ul>
            </div>
          </div>
        </Col>

        <Col xl={12}>
          <div className="mc-card">


            {/* Filters */}
            <Row className="mb-4">
              <Col md={3}>
                <Form.Control
                  type="text"
                  placeholder="Search FSF-ID / Name / CNIC / Institute"
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value
                    )
                  }
                />
              </Col>

              <Col md={2}>
                <Form.Select
                  value={statusFilter}
                  onChange={changeFilter(
                    setStatusFilter
                  )}
                >
                  <option value="">
                    All Status
                  </option>

                  {filterOptions.statuses.map(
                    (status) => (
                      <option
                        key={status}
                        value={status}
                      >
                        {status}
                      </option>
                    )
                  )}
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Select
                  value={courseFilter}
                  onChange={changeFilter(
                    setCourseFilter
                  )}
                >
                  <option value="">
                    All Courses
                  </option>

                  {filterOptions.courses.map(
                    (course) => (
                      <option
                        key={course.id}
                        value={course.id}
                      >
                        {course.course_name}
                      </option>
                    )
                  )}
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Select
                  value={instituteFilter}
                  onChange={changeFilter(
                    setInstituteFilter
                  )}
                >
                  <option value="">
                    All Institutes
                  </option>

                  {filterOptions.institutes.map(
                    (institute) => (
                      <option
                        key={institute.id}
                        value={
                          institute.id
                        }
                      >
                        {institute.name}
                      </option>
                    )
                  )}
                </Form.Select>
              </Col>

              <Col md={1}>
                <Button
                  variant="secondary"
                  onClick={
                    resetFilters
                  }
                >
                  Reset
                </Button>
              </Col>
            </Row>

            <Row>
              <Col xl={12}>
                {busy && (
                  <h5 className="text-center my-3">
                    <Spinner animation="border" variant="success" /> Loading Data...
                  </h5>
                )}
                {!busy && trainings.length === 0 && (
                  <h5 className="text-center my-3">No trainings found.</h5>
                )}
                <div
                  style={{
                    opacity: busy ? 0.4 : 1,
                    pointerEvents: busy ? "none" : "auto",
                  }}
                >
                <TrainingTableComponent
                  thead={[
                    "Family",
                    "Student's Name",
                    "Relation",
                    "Institute's Name",
                    "Training Name",
                    "Training Duration",
                    "Total Fees",
                    "Status",
                    "Actions",
                  ]}
                  tbody={trainings}
                />
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
              <Button
                style={{ backgroundColor: "#077834" }}
                disabled={busy || pagination.current_page <= 1}
                onClick={() => setPage(pagination.current_page - 1)}
              >
                Prev
              </Button>
              <span>
                Page {pagination.current_page} of {pagination.last_page}
                {pagination.total > 0 &&
                  ` (${pagination.from}-${pagination.to} of ${pagination.total})`}
              </span>
              <Button
                style={{ backgroundColor: "#077834" }}
                disabled={busy || pagination.current_page >= pagination.last_page}
                onClick={() => setPage(pagination.current_page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}
